import { PlusIcon } from '@commenda/icons';
import { cn } from '@commenda/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import React from 'react';
import { ADD_NEW_ITEM_SELECT_MENU } from './constants';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  hideIcon?: boolean;
  error?: string;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, hideIcon = false, error, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full box-border items-center justify-between rounded-md px-xl py-2.5 bg-bg-primary text-text-primary txt-small-regular data-[placeholder]:text-text-placeholder ring-0 transition-colors',
      'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)]',
      'border disabled:border-border-disabled [&>span]:line-clamp-1 gap-2',
      // When open, show focus ring
      'data-[state="open"]:ring-4 data-[state="open"]:ring-[var(--color-bg-brand-hover)] data-[state="open"]:border-fg-brand',
      'aria-expanded:ring-4 aria-expanded:ring-[var(--color-bg-brand-hover)] aria-expanded:border-fg-brand',
      error
        ? 'border-border-error hover:border-border-error focus-visible:border-border-error-hover'
        : 'border-border-primary hover:border-border-primary-hover focus-visible:border-fg-brand',
      className
    )}
    {...(hideIcon ? { onClick: (e) => e.preventDefault() } : {})}
    {...props}
  >
    {children}
    {!hideIcon ? (
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-4 shrink-0 text-fg-quaternary" />
      </SelectPrimitive.Icon>
    ) : null}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-[40vh] py-xs overflow-hidden rounded-md border border-border-primary bg-bg-primary text-text-primary shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      sideOffset={0}
      alignOffset={0}
      avoidCollisions
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          position === 'popper' &&
            'w-full min-w-[var(--radix-select-trigger-width)]'
        )}
        style={{
          margin: '-1px', // Compensate for the border
        }}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'py-1.5 px-2 pr-2 caps-xs-medium text-text-quaternary uppercase',
      className
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    selected?: boolean;
    description?: React.ReactNode;
  }
>(({ className, children, selected, description, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-md px-lg py-md txt-small-medium outline-none transition-colors text-text-tertiary hover:text-text-secondary-hover focus:text-text-secondary-hover focus:bg-bg-primary-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    {selected && (
      <span className="absolute right-2 h-5 w-5 flex items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-4 shrink-0" />
        </SelectPrimitive.ItemIndicator>
      </span>
    )}

    <span className="flex w-full flex-col">
      <SelectPrimitive.ItemText className="w-full">
        {children}
      </SelectPrimitive.ItemText>
      {description && (
        <span className="txt-extra-small-regular text-text-tertiary">
          {description}
        </span>
      )}
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

interface SelectAddItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>,
    'onSelect' | 'value'
  > {
  text?: string;
  onAddItem: () => void;
}

const SelectAddItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectAddItemProps
>(
  (
    { className, text = 'Add new item', onAddItem: _onAddItem, ...props },
    ref
  ) => {
    return (
      <SelectPrimitive.Item
        ref={ref}
        value={ADD_NEW_ITEM_SELECT_MENU}
        className={cn(
          'flex w-full cursor-pointer select-none items-center py-[11px] px-1.5 txt-medium-medium outline-none text-text-primary hover:bg-bg-active-light focus:bg-bg-active-light focus:text-text-primary rounded-sm gap-1',
          className
        )}
        {...props}
      >
        <span className="flex items-center justify-center">
          <PlusIcon className="h-5 w-5 text-fg-quinary" />
        </span>
        <span>{text}</span>
      </SelectPrimitive.Item>
    );
  }
);

SelectAddItem.displayName = 'SelectAddItem';

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border-primary', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectAddItem,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
